import { calculateDownPayment, getInterestRate } from '@atc/wallet-payment-fns';
import { defaultPaymentInfo } from 'axl-config';
import { syncWithWalletStorage } from './syncWallet';
export const updateStoredValues = _ref => {
  let {
    paymentsInfo = {},
    payments,
    walletStorageService,
    actionCode,
    isLoggedIn
  } = _ref;
  let newInteractions;
  if (actionCode) {
    if (actionCode === 'addHideReceiptListing') {
      if (!paymentsInfo?.interactions?.hideReceipt) {
        newInteractions = {
          ...paymentsInfo?.interactions,
          hideReceipt: [payments]
        };
      } else {
        newInteractions = {
          ...paymentsInfo?.interactions,
          hideReceipt: [...paymentsInfo.interactions.hideReceipt, payments]
        };
      }
    }
    if (actionCode === 'removeHideReceiptListing') {
      if (!paymentsInfo?.interactions?.hideReceipt) {
        newInteractions = {
          ...paymentsInfo?.interactions,
          hideReceipt: []
        };
      } else {
        newInteractions = {
          ...paymentsInfo?.interactions,
          hideReceipt: [...paymentsInfo.interactions.hideReceipt.filter(item => item !== payments)]
        };
      }
    }
  } else {
    newInteractions = {
      ...paymentsInfo?.interactions,
      ...payments.interactions
    };
  }
  const newPaymentsInfo = {
    ...paymentsInfo,
    ...payments,
    tradeIn: {
      ...paymentsInfo.tradeIn,
      ...payments.tradeIn
    },
    creditVerification: {
      ...paymentsInfo.creditVerification,
      ...payments.creditVerification
    },
    interactions: newInteractions
  };

  // update Wallet Storage
  if (walletStorageService) {
    const options = {
      action: 'update',
      walletStorageService,
      paymentInfo: {
        ...newPaymentsInfo
      },
      isLoggedIn
    };
    return syncWithWalletStorage(options);
  }
  return {};
};
export const hasValidPayload = (payload, keys) => Object.keys(payload).reduce((acc, key) => {
  if (keys.includes(key)) {
    acc = true;
  }
  return acc;
}, false);
export const calculateMonthlyBudget = async (paymentFetcher, payload, paymentInfo) => {
  const paymentsState = {
    ...paymentInfo,
    ...payload
  };
  const {
    loanTerm,
    budget = 0,
    tradeIn,
    interestRate,
    creditType
  } = paymentsState;
  const tradeInParam = tradeIn?.value || 0;
  const amountOwedParam = tradeIn?.amountOwed || 0;
  const interestRateParam = getInterestRate(interestRate);
  const downPaymentParam = calculateDownPayment(budget, paymentsState);
  const paymentParams = `budget=${budget}&terms=${loanTerm}&customRate=${interestRateParam}&downPayment=${downPaymentParam}&tradeInValue=${tradeInParam}&amountOwed=${amountOwedParam}&creditRange=${creditType}`;
  const {
    rates = {}
  } = (await paymentFetcher(paymentParams)) || {};

  // monthly payment from response
  return rates?.[creditType]?.custom?.[loanTerm]?.[0]?.payment;
};
export const calculateTotalBudget = async (paymentFetcher, payload, paymentInfo) => {
  const paymentsState = {
    ...paymentInfo,
    ...payload
  };
  const {
    loanTerm,
    monthlyBudget = 0,
    tradeIn,
    interestRate,
    downPayment,
    downPaymentPercent
  } = paymentsState;
  const tradeInParam = tradeIn?.value || 0;
  const amountOwedParam = tradeIn?.amountOwed || 0;
  const interestRateParam = getInterestRate(interestRate);
  const downPaymentParam = downPayment ? `downPayment=${downPayment}` : `downPaymentPercent=${downPaymentPercent * 100}`;
  const budgetApiParams = `monthlyPayment=${monthlyBudget}&tradeInValue=${tradeInParam}&amountOwed=${amountOwedParam}&loanTerm=${loanTerm}&interestRate=${interestRateParam}&${downPaymentParam}`;
  const {
    totalBudget
  } = (await paymentFetcher(budgetApiParams, true)) || {};
  return totalBudget;
};

// remove Credit Verification from local storage if they are expired
export const expireCreditVerification = paymentsInfo => {
  const now = new Date().getTime();
  const creditInfo = paymentsInfo?.creditVerification?.verifiedDate;
  const creditVerifiedDate = new Date(creditInfo || now);
  creditVerifiedDate.setDate(creditVerifiedDate.getDate() + 30);
  const isValid = now < creditVerifiedDate.getTime();
  if (!isValid) {
    paymentsInfo.creditVerification = {};
  }
};

// removes credit apps from local storage if they are expired
export const expireCreditAppDeals = paymentsInfo => {
  if (paymentsInfo?.creditAppDeals?.length > 0) {
    const now = new Date().getTime();
    paymentsInfo.creditAppDeals = [...paymentsInfo.creditAppDeals.filter(creditApp => now < creditApp.expiry)];
  }
};

// Allow response to be passed as is (as an array) to reuse mapping logic here
export const mapDeals = dealsArray => dealsArray.reduce((acc, deal) => {
  acc[deal.id] = {
    ...deal
  };
  return acc;
}, {});

// collect and count all instances of the following flags
// Price change, Monthly payment change, Deal type change, Change of vehicle, Credit App status update
export const aggregateDealNotifications = dealsArray => {
  const PRICE_CHANGE = 'priceChangeFlag';
  const VEHICLE_CHANGE = 'sellingVehicleChangeFlag';
  const DEAL_TYPE_CHANGE = 'dealTypeChangeFlag';
  const PAYMENT_CHANGE = 'paymentChangeFlag';
  const CREDIT_APP_CHANGE = 'creditAppStatusChangeFlag';
  const notifications = {};
  let totalCount = 0;
  dealsArray.forEach(deal => {
    if (deal.vehicle?.[PRICE_CHANGE]) {
      notifications[PRICE_CHANGE] = (notifications[PRICE_CHANGE] || 0) + 1;
      totalCount += 1;
    }
    if (deal.vehicle?.[VEHICLE_CHANGE]) {
      notifications[VEHICLE_CHANGE] = (notifications[VEHICLE_CHANGE] || 0) + 1;
      totalCount += 1;
    }
    if (deal.deal?.[DEAL_TYPE_CHANGE]) {
      notifications[DEAL_TYPE_CHANGE] = (notifications[DEAL_TYPE_CHANGE] || 0) + 1;
      totalCount += 1;
    }
    if (deal.deal?.[PAYMENT_CHANGE]) {
      notifications[PAYMENT_CHANGE] = (notifications[PAYMENT_CHANGE] || 0) + 1;
      totalCount += 1;
    }
    if (deal.creditApp?.isDecisionChanged) {
      notifications[CREDIT_APP_CHANGE] = (notifications[CREDIT_APP_CHANGE] || 0) + 1;
      totalCount += 1;
    }
  });
  if (totalCount) {
    notifications.total = totalCount;
  }
  return notifications;
};
export const validatePaymentsInfo = (initialState, paymentsInfo, myWalletConfigs) => {
  const validatedPaymentsInfo = {
    ...paymentsInfo
  };

  // delete old irrelevant keys from payments info
  // NOTE: any new keys should be added to defaultState if they want to persist
  const acceptedKeys = Object.keys(initialState);
  Object.keys(validatedPaymentsInfo).forEach(key => {
    // need to keep timestamp for local storage expiration
    if (!acceptedKeys.includes(key) && key !== 'timeStamp') {
      delete validatedPaymentsInfo[key];
    }
  });

  // if payment info is present but some of the required values are null, set them to default values
  const {
    creditType,
    downPayment,
    downPaymentPercent,
    loanTerm,
    interestRate
  } = validatedPaymentsInfo;
  if (creditType == null || loanTerm == null || downPayment == null && downPaymentPercent == null || Object.keys(interestRate).length === 0) {
    Object.keys(validatedPaymentsInfo).forEach(key => {
      if (validatedPaymentsInfo[key] == null || Object.keys(validatedPaymentsInfo[key]).length === 0) {
        validatedPaymentsInfo[key] = myWalletConfigs?.payments?.[key] || defaultPaymentInfo[key];
      }
    });
  }
  expireCreditAppDeals(validatedPaymentsInfo);
  expireCreditVerification(validatedPaymentsInfo);
  return validatedPaymentsInfo;
};